import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  Link,
  Skeleton,
  alpha,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Launch as LaunchIcon, Visibility as VisibilityIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { ColumnConfig, CurrentTabRow, formatDateEnhanced, getEmailStatus } from '../../utils/customerSentimentutils';
import { CustomerRequestResponse, SolutionStoryResponse, SolutionStoryListItem } from '../../services/api';
import { TableHeader } from './TableHeader';
import { TypeChip } from './TypeChip';
import { FileViewer } from '../../components/modules/FileViewer';
import { normalizeZohoIssueLinkUrl, zohoIssueLinkProps } from './zohoIssueUrl';
import { useMuiTableColumnResize } from '../../hooks/useMuiTableColumnResize';
import { CUSTOMER_SENTIMENT_DEFAULT_COLUMN_PX } from './customerSentimentColumnWidths';

/** When story is Draft or Failed, show email column as Pending (not yet applicable). */
function getSolutionStoryEmailStatusDisplay(row: SolutionStoryListItem): string {
  const st = (row.status || '').trim().toLowerCase();
  if (st === 'draft' || st === 'failed') return 'Pending';
  return (row.email_status || '').trim();
}

/** Full cell text for hover tooltips on the Solution Stories table (tab 5). */
function buildSolutionStoryTooltip(
  column: ColumnConfig,
  request: CurrentTabRow,
  value: unknown,
  displayValue: string
): string {
  const row = request as SolutionStoryListItem & CustomerRequestResponse;
  switch (column.id) {
    case 'request_number': {
      const st = (row as SolutionStoryListItem).status || '';
      if (st === 'Draft') return 'Resume filling the solution story draft';
      if (st === 'Failed') return 'Retry submitting the solution story';
      return value != null && String(value).trim() !== '' ? String(value) : '—';
    }
    case 'solution_story_link':
      return value ? String(value) : displayValue;
    case 'email_status':
      return getSolutionStoryEmailStatusDisplay(row as SolutionStoryListItem) || '—';
    default:
      if (column.isDate && value != null && value !== '')
        return `${displayValue} (${String(value)})`;
      if (displayValue === '-' && (value == null || value === '')) return '—';
      return String(displayValue);
  }
}

/** Zoho issue column: link, chip, or failed + retry (Sign-Off Requests tab 1, Escalations tab 3). */
export function ZohoIssueWithRetry({
  zohoStatus,
  zohoLink,
  showRetry,
  isRetrying,
  onRetry,
}: {
  zohoStatus: string;
  zohoLink?: string | null;
  showRetry: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  const showLink = Boolean(zohoLink && zohoStatus !== 'N/A' && !zohoStatus.includes('Failed'));
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        py: 0.5,
        flexWrap: 'nowrap',
      }}
    >
      {showLink ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Link
            {...zohoIssueLinkProps}
            href={normalizeZohoIssueLinkUrl(zohoLink!)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: SEARCE_COLORS.primary,
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View in Zoho
            <LaunchIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      ) : (
        <Box sx={{ display: 'inline-flex' }}>
          <TypeChip type={zohoStatus} />
        </Box>
      )}
      {showRetry && (
        <Tooltip title={isRetrying ? 'Retrying…' : 'Retry Zoho issue'}>
          <span>
            <IconButton
              size="small"
              disabled={isRetrying}
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              sx={{ padding: '4px', color: SEARCE_COLORS.primary }}
              aria-label="Retry Zoho issue"
            >
              {isRetrying ? (
                <CircularProgress size={20} thickness={4} sx={{ color: SEARCE_COLORS.primary }} />
              ) : (
                <ReplayIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

/** Shared layout for EMAIL STATUS column with optional retry (Sign-Off tab 1, Solution Stories tab 5). */
function EmailStatusWithRetry({
  displayEmailStatus,
  showRetry,
  isRetrying,
  onRetry,
}: {
  displayEmailStatus: string;
  showRetry: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        py: 0.5,
        flexWrap: 'nowrap',
      }}
    >
      {displayEmailStatus ? (
        <Box sx={{ display: 'inline-flex' }}>
          <TypeChip type={displayEmailStatus} />
        </Box>
      ) : (
        <Typography sx={{ fontSize: '0.875rem', color: SEARCE_COLORS.textSecondary }}>—</Typography>
      )}
      {showRetry && (
        <Tooltip title={isRetrying ? 'Sending…' : 'Retry'}>
          <span>
            <IconButton
              size="small"
              disabled={isRetrying}
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              sx={{
                padding: '4px',
                color: SEARCE_COLORS.primary,
              }}
              aria-label="Retry email"
            >
              {isRetrying ? (
                <CircularProgress size={20} thickness={4} sx={{ color: SEARCE_COLORS.primary }} />
              ) : (
                <ReplayIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

interface StandardTableProps {
  columns: ColumnConfig[];
  data: CurrentTabRow[];
  loading: boolean;
  error: string | null;
  activeTab: number;
  onSort: (columnId: string) => void;
  onFilterClick: (columnId: string, event: React.MouseEvent<HTMLElement>) => void;
  isFilterActive: (columnId: string) => boolean;
  renderFilterMenu: (columnId: string, columnLabel: string) => React.ReactNode;
  onViewClick: (request: CustomerRequestResponse | SolutionStoryResponse | SolutionStoryListItem) => void;
  /** Resend notification email (tab 1 sign-off failed send, tab 5 solution story failed email_status) */
  onRetryEmail?: (row: SolutionStoryListItem | CustomerRequestResponse) => void;
  retryingEmailRequestId?: string | null;
  /** Retry Zoho after Failed Issue Creation (tab 1 feedback, tab 3 escalation) */
  onRetryZoho?: (req: CustomerRequestResponse, scope: 'escalation' | 'feedback_failed_all') => void;
  retryingZohoKey?: string | null;
}

export const StandardTable: React.FC<StandardTableProps> = ({
  columns,
  data,
  loading,
  error,
  activeTab,
  onSort,
  onFilterClick,
  isFilterActive,
  renderFilterMenu,
  onViewClick,
  onRetryEmail,
  retryingEmailRequestId,
  onRetryZoho,
  retryingZohoKey,
}) => {
  const visibleColumns = columns.filter((column) => !(column.id === 'id' && activeTab === 5));
  const colResize = useMuiTableColumnResize(CUSTOMER_SENTIMENT_DEFAULT_COLUMN_PX);
  const [fileViewerOpen, setFileViewerOpen] = React.useState(false);
  const [fileViewerPath, setFileViewerPath] = React.useState<string>('');
  const [fileViewerName, setFileViewerName] = React.useState<string>('');
  const getColumnWidth = (columnId: string): string | undefined => {
    // Solution Stories (tab 5): compact columns; STORY ID wide enough for a single-line id
    if (activeTab === 5) {
      switch (columnId) {
        case 'request_number':
          return '11%';
        case 'status':
          return '6%';
        case 'submitted_at':
        case 'submitter_email':
        case 'project_owner':
          return '11%';
        case 'account_name':
          return '16%';
        case 'project_code':
          return '9%';
        case 'project_name':
          return '10%';
        case 'opportunity':
          return '14%';
        case 'solution_story_link':
          return '10%';
        case 'email_status':
          return '10%';
      }
    }
    switch (columnId) {
      case 'request_number':
        return '16%';
      case 'project_code':
        return '10%';
      case 'project_name':
        // Solution Stories: narrower project name, wider opportunity (see opportunity case)
        return activeTab === 5 ? '11%' : '18%';
      case 'opportunity':
        return activeTab === 5 ? '16%' : 'auto';
      case 'account':
      case 'customer_spoc':
        return '15%';
      case 'project_account_bu':
        return '10%';
      case 'date':
      case 'date_raised':
      case 'closure_date':
        return '12%';
      case 'type':
      case 'level':
      case 'status':
        return '12%';
      case 'customer_response_status':
        return '12%';
      case 'email_status':
        return '12%';
      case 'zoho_status':
        return '12%';
      case 'escalated_by':
        return '12%';
      case 'link':
        return '18%';
      default:
        return 'auto';
    }
  };

  /** Minimum width so header label + filter control stay visible (table is tableLayout: fixed). */
  const getColumnMinWidth = (columnId: string): string => {
    if (activeTab === 5) {
      switch (columnId) {
        case 'request_number':
          return '140px';
        case 'status':
          return '88px';
        case 'account_name':
        case 'submitter_email':
        case 'submitted_at':
        case 'project_owner':
          return '172px';
        case 'project_code':
          return '100px';
        case 'project_name':
          return '136px';
        case 'opportunity':
          return '144px';
        case 'solution_story_link':
          return '152px';
        case 'email_status':
          return '96px';
      }
    }

    const XL = '180px';
    const LG = '150px';
    const MD = '128px';
    const SM = '112px';
    const XS = '104px';

    switch (columnId) {
      case 'solution_story_status':
      case 'customer_response_status':
      case 'customer_poc_designation':
      case 'deliveryClosure':
      case 'areasForImprovement':
      case 'specificContributions':
      case 'teamResponsiveness':
      case 'overallSatisfaction':
        return XL;
      case 'project_name':
      case 'account_name':
      case 'customer_spoc':
      case 'customer_poc_name':
      case 'customerName':
      case 'customerEmail':
      case 'escalated_by':
      case 'submitter_email':
      case 'project_owner':
      case 'feedback_status':
      case 'email_status':
      case 'submission_date':
      case 'date_testimony_received':
      case 'submitter_name':
        return LG;
      case 'request_number':
      case 'requestNumber':
      case 'opportunity':
      case 'solution_story_link':
      case 'public_testimonial_link':
      case 'account':
      case 'project_account_bu':
      case 'closure_date':
      case 'submitted_at':
      case 'created_at':
      case 'date':
      case 'date_raised':
        return MD;
      case 'project_code':
      case 'projectCode':
      case 'region':
      case 'zoho_status':
      case 'zohoStatus':
      case 'status':
      case 'level':
      case 'type':
      case 'file_url':
      case 'link':
      case 'request_status':
        return SM;
      case 'deliveryCompletion':
      case 'deliveryTimeline':
      case 'teamTechnical':
      case 'teamSupport':
        return '120px';
      case 'id':
        return MD;
      default:
        return XS;
    }
  };

  // Tab indices: 1=Sign-Off Requests, 3=Escalations, 4=Appreciations, 5=Solution Stories, 6=Public Testimonials (StandardTable not used for 0 or 2)
  const getEmptyMessage = () => {
    switch (activeTab) {
      case 1:
        return 'No sign-off requests found.';
      case 3:
        return 'No escalations found.';
      case 4:
        return 'No appreciations found.';
      case 5:
        return 'No solution stories found.';
      case 6:
        return 'No public testimonials found.';
      default:
        return 'No data found.';
    }
  };

  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableHeader
            columns={visibleColumns}
            onSort={onSort}
            onFilterClick={onFilterClick}
            isFilterActive={isFilterActive}
            renderFilterMenu={renderFilterMenu}
            getColumnWidth={getColumnWidth}
            getColumnMinWidth={getColumnMinWidth}
            getColumnWidthPx={colResize.getWidth}
            onBeginColumnResize={colResize.beginResize}
            resizingColumnId={colResize.resizingColumnId}
          />
        </TableHead>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {visibleColumns.map((_, cellIndex) => (
                  <TableCell key={cellIndex} align="center" sx={{ py: 1 }}>
                    <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : error ? (
            <TableRow>
              <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                <Typography color="error">{error}</Typography>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary">
                  {getEmptyMessage()}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((request, idx) => {
              const requestKey = (request as CustomerRequestResponse).id;
              return (
                <TableRow
                  key={requestKey || `row-${idx}`}
                  sx={{
                    '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.02) },
                    '&:nth-of-type(even)': { backgroundColor: alpha(SEARCE_COLORS.neutral_100, 0.5) },
                  }}
                >
                  {visibleColumns.map((column) => {
                    const value = column.field(request);
                    const displayValue = column.isDate 
                      ? formatDateEnhanced(value) 
                      : (value || '-');

                    const solutionStoryTooltip =
                      activeTab === 5 ? buildSolutionStoryTooltip(column, request, value, displayValue) : '';
                    
                    const isRequestId = column.id === 'request_number';
                    // Don't make request_number clickable on solution story tab (tab 5) since it uses Link component there
                    const isClickable = (isRequestId && activeTab !== 5);
                    
                    return (
                      <TableCell
                        key={column.id}
                        align="center"
                        onClick={isClickable ? () => onViewClick(request as CustomerRequestResponse) : undefined}
                        title={
                          activeTab === 5
                            ? undefined
                            : column.id === 'zoho_status'
                              ? undefined
                              : displayValue
                        }
                        sx={{
                          color: SEARCE_COLORS.textPrimary,
                          fontWeight: 400,
                          py: 1,
                          fontSize: '0.875rem',
                          ...colResize.colWidthSx(column.id),
                          overflow:
                            column.id === 'customer_response_status' || column.id === 'email_status'
                              ? 'visible'
                              : activeTab === 5 && column.id === 'request_number'
                                ? 'visible'
                                : 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace:
                            activeTab === 5 && column.id === 'request_number' ? 'nowrap' : undefined,
                          position: 'relative',
                          ...(isClickable && {
                            cursor: 'pointer',
                            color: SEARCE_COLORS.primary,
                            fontWeight: 500,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: SEARCE_COLORS.primary,
                            },
                          }),
                        }}
                      >
                        {(() => {
                          const cellContent = (() => {
                          // Render STATUS as chip for escalation tab (tab 3) and solution stories (tab 5)
                          if (column.id === 'status' && (activeTab === 3 || activeTab === 5) && value) {
                            const statusChip = (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box>
                                  <TypeChip type={String(value)} />
                                </Box>
                              </Box>
                            );
                            return activeTab === 5 ? (
                              statusChip
                            ) : (
                              <Tooltip title={displayValue}>{statusChip}</Tooltip>
                            );
                          }

                          if (column.id === 'email_status' && activeTab === 1) {
                            const req = request as CustomerRequestResponse;
                            const displayEmailStatus = getEmailStatus(req);
                            const showRetry =
                              displayEmailStatus === 'Failed' && Boolean(onRetryEmail);
                            const isRetrying = retryingEmailRequestId === req.id;
                            return (
                              <EmailStatusWithRetry
                                displayEmailStatus={displayEmailStatus}
                                showRetry={showRetry}
                                isRetrying={isRetrying}
                                onRetry={() => onRetryEmail?.(req)}
                              />
                            );
                          }

                          if (column.id === 'email_status' && activeTab === 5) {
                            const row = request as SolutionStoryListItem;
                            const es = (row.email_status || '').trim();
                            const st = row.status || '';
                            const displayEmailStatus = getSolutionStoryEmailStatusDisplay(row);
                            const showRetry =
                              displayEmailStatus !== 'Pending' &&
                              es.toLowerCase() === 'failed' &&
                              (st === 'Submitted' || st === 'Created') &&
                              Boolean(onRetryEmail);
                            const isRetrying = retryingEmailRequestId === row.id;
                            return (
                              <EmailStatusWithRetry
                                displayEmailStatus={displayEmailStatus}
                                showRetry={showRetry}
                                isRetrying={isRetrying}
                                onRetry={() => onRetryEmail?.(row)}
                              />
                            );
                          }
                          
                          // Render STORY ID (request_number) column as clickable link for solution story tab
                          if (column.id === 'request_number' && activeTab === 5) {
                            const row = request as SolutionStoryListItem;
                            const storyId = value || ''; // request_number or blank
                            const st = row.status || '';
                            const draftOrRetryPath = row.id
                              ? `/customer-feedback?type=solution-story&draftId=${row.id}`
                              : '/customer-feedback?type=solution-story';

                            const linkSx = {
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: SEARCE_COLORS.primary,
                              textDecoration: 'none',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            } as const;

                            if (st === 'Draft') {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Link component={RouterLink} to={draftOrRetryPath} sx={linkSx}>
                                    Resume Filling
                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                  </Link>
                                </Box>
                              );
                            }
                            if (st === 'Failed') {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Link component={RouterLink} to={draftOrRetryPath} sx={linkSx}>
                                    Retry Submission
                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                  </Link>
                                </Box>
                              );
                            }

                            if (storyId && row.id) {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Box
                                    onClick={() => onViewClick(row as CustomerRequestResponse)}
                                    sx={{
                                      ...linkSx,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {storyId}
                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                  </Box>
                                </Box>
                              );
                            }
                            // If no request_number, show blank (not clickable)
                            return <Box sx={{ display: 'flex', justifyContent: 'center' }}>{''}</Box>;
                          }
                          
                          // Render TESTIMONIAL ID (request_number) as regular text (not a link) for public testimonial tab
                          if (column.id === 'request_number' && activeTab === 6) {
                            const testimonialId = value || '';
                            return (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Typography
                                  sx={{
                                    color: SEARCE_COLORS.textPrimary,
                                    fontWeight: 400,
                                    fontSize: '0.875rem',
                                    textDecoration: 'none',
                                  }}
                                >
                                  {testimonialId || '-'}
                                </Typography>
                              </Box>
                            );
                          }
                          
                          // Hide the 'id' column (used only for linking, not displayed)
                          if (column.id === 'id' && activeTab === 5) {
                            return null;
                          }
                          
                          // Render Link to Story column as clickable external link for solution story tab
                          if (column.id === 'solution_story_link' && activeTab === 5 && value) {
                            const linkUrl = String(value);
                            if (linkUrl && (linkUrl.startsWith('http://') || linkUrl.startsWith('https://'))) {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Link
                                    href={linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      color: SEARCE_COLORS.primary,
                                      textDecoration: 'none',
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    Link
                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                  </Link>
                                </Box>
                              );
                            }
                            return <Box sx={{ display: 'flex', justifyContent: 'center' }}>{displayValue}</Box>;
                          }
                          
                          // Render public testimonial link column as clickable "Link" text
                          if (column.id === 'public_testimonial_link' && activeTab === 6 && value) {
                            const linkUrl = String(value);
                            if (linkUrl && (linkUrl.startsWith('http://') || linkUrl.startsWith('https://'))) {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Link
                                    href={linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      color: SEARCE_COLORS.primary,
                                      textDecoration: 'none',
                                      fontWeight: 500,
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    Link
                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                  </Link>
                                </Box>
                              );
                            }
                            return <Box sx={{ display: 'flex', justifyContent: 'center' }}>{displayValue}</Box>;
                          }
                          
                          // Render file column with view icon for public testimonial tab
                          if (column.id === 'file_url' && activeTab === 6) {
                            const fileUrl = value ? String(value) : null;
                            if (fileUrl) {
                              // Extract file path from GCS URL (same logic as FileViewer)
                              const extractFilePath = (url: string): string => {
                                if (url.startsWith('gs://')) {
                                  const parts = url.replace('gs://', '').split('/');
                                  return parts.slice(1).join('/'); // Remove bucket name
                                }
                                if (url.includes('storage.googleapis.com')) {
                                  const match = url.match(/\/o\/(.+?)(?:\?|$)/);
                                  return match ? decodeURIComponent(match[1]) : url;
                                }
                                return url;
                              };
                              
                              const filePath = extractFilePath(fileUrl);
                              const fileName = filePath.split('/').pop() || 'file';
                              
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Box
                                    onClick={() => {
                                      setFileViewerPath(filePath);
                                      setFileViewerName(fileName);
                                      setFileViewerOpen(true);
                                    }}
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      cursor: 'pointer',
                                      color: SEARCE_COLORS.primary,
                                      '&:hover': {
                                        opacity: 0.8,
                                      },
                                    }}
                                  >
                                    <VisibilityIcon sx={{ fontSize: 18 }} />
                                  </Box>
                                </Box>
                              );
                            }
                            return <Box sx={{ display: 'flex', justifyContent: 'center' }}>{'-'}</Box>;
                          }
                          
                          // Zoho: link, chip, or failed + retry (tab 1 Sign-Off Requests, tab 3 Escalations)
                          if (column.id === 'zoho_status' && value) {
                            const req = request as CustomerRequestResponse;
                            let zohoLink: string | undefined;
                            if (req.request_type === 'escalation' && req.escalation_detail?.zoho_issue_link) {
                              zohoLink = req.escalation_detail.zoho_issue_link;
                            } else if (req.request_type === 'feedback' && req.feedback_responses?.[0]?.zoho_issue_link) {
                              zohoLink = req.feedback_responses[0].zoho_issue_link;
                            } else {
                              zohoLink = req.zoho_issue_link;
                            }
                            const zohoStatusStr = String(value);
                            const supportsRetry =
                              (activeTab === 1 && req.request_type === 'feedback') ||
                              (activeTab === 3 && req.request_type === 'escalation');
                            const scope: 'escalation' | 'feedback_failed_all' =
                              activeTab === 3 ? 'escalation' : 'feedback_failed_all';
                            const showRetry =
                              supportsRetry &&
                              zohoStatusStr.includes('Failed') &&
                              Boolean(onRetryZoho);
                            const rk = `${req.id}:${scope}`;
                            const isRetryingZoho = retryingZohoKey === rk;
                            return (
                              <ZohoIssueWithRetry
                                zohoStatus={zohoStatusStr}
                                zohoLink={zohoLink}
                                showRetry={showRetry}
                                isRetrying={isRetryingZoho}
                                onRetry={() => onRetryZoho?.(req, scope)}
                              />
                            );
                          }
                          
                          // Render other chip columns (type, level, customer_response_status, email_status) with TypeChip/badges for Sign-Off Request, Escalation, Appreciation tabs
                          if ((column.id === 'type' || column.id === 'level' || 
                               column.id === 'customer_response_status' || 
                               column.id === 'email_status') && value) {
                            const req = request as CustomerRequestResponse;
                            // Special handling for customer_response_status: show count badge when "Received"
                            if (column.id === 'customer_response_status' && value === 'Received') {
                              const feedbackCount = req.feedback_responses?.length || req.feedback_detail?.response_count || 0;
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', padding: '4px 4px' }}>
                                  <Tooltip title={`${displayValue} - ${feedbackCount} feedback${feedbackCount !== 1 ? 's' : ''} received`}>
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                      <TypeChip type={String(value)} />
                                      {feedbackCount > 0 && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: -4,
                                            right: -4,
                                            backgroundColor: alpha(SEARCE_COLORS.textSecondary, 0.7),
                                            color: SEARCE_COLORS.white,
                                            borderRadius: '50%',
                                            minWidth: '18px',
                                            height: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            border: `1.5px solid ${SEARCE_COLORS.white}`,
                                            boxShadow: `0 1px 2px ${alpha('#000', 0.15)}`,
                                            zIndex: 1,
                                          }}
                                        >
                                          {feedbackCount}
                                        </Box>
                                      )}
                                    </Box>
                                  </Tooltip>
                                </Box>
                              );
                            }
                            
                            return (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Tooltip title={displayValue}>
                                  <Box>
                                    <TypeChip type={String(value)} />
                                  </Box>
                                </Tooltip>
                              </Box>
                            );
                          }
                          
                          // Default text rendering
                          return (
                            <Box
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                              }}
                            >
                              {displayValue}
                            </Box>
                          );
                          })();

                          if (activeTab !== 5) {
                            return cellContent;
                          }

                          return (
                            <Tooltip
                              title={solutionStoryTooltip}
                              arrow
                              placement="top-start"
                              enterDelay={300}
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 560,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  },
                                },
                              }}
                            >
                              <Box component="span" sx={{ display: 'block', width: '100%', minWidth: 0 }}>
                                {cellContent}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      
      {/* File Viewer Modal for Public Testimonials */}
      {activeTab === 6 && (
        <FileViewer
          open={fileViewerOpen}
          onClose={() => {
            setFileViewerOpen(false);
            setFileViewerPath('');
            setFileViewerName('');
          }}
          filePath={fileViewerPath}
          fileName={fileViewerName}
        />
      )}
    </TableContainer>
  );
};
